import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Box,
  Divider,
  Grid,
} from "@mui/material";

const SignUp = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    company: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email, password, company } = formData;

    if (!firstName || !lastName || !email || !password || !company || !role) {
      setError("Please fill all required fields and select a role.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("http://localhost:5000/api/users/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          role,
          displayName: `${firstName} ${lastName}`,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Signup failed.");

      setError("");
      setSuccess(
        "Account created! Check your email to verify your account before logging in.",
      );
      setTimeout(() => navigate("/login"), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "#F7F8FC",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: -120,
          right: -120,
          width: 480,
          height: 480,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(229,0,0,0.07) 0%, transparent 70%)",
          pointerEvents: "none",
        },
      }}
    >
      <Card
        sx={{
          maxWidth: 480,
          width: "100%",
          boxShadow: "0 8px 40px rgba(0,0,0,0.1)",
          border: "1px solid #F0F0F5",
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Logo */}
          <Box textAlign="center" mb={3}>
            <img src="/logo.png" alt="Logo" style={{ height: 44 }} />
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            textAlign="center"
            color="text.primary"
            gutterBottom
          >
            Create your account
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            mb={3}
          >
            Join tour owners and distributors on Sabre.
          </Typography>

          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2, borderRadius: 2, fontSize: "0.85rem" }}
            >
              {success}
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2, fontSize: "0.85rem" }}
            >
              {error}
            </Alert>
          )}

          {/* Role selector */}
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              display: "block",
              mb: 1,
            }}
          >
            I am a
          </Typography>
          <ToggleButtonGroup
            value={role}
            exclusive
            onChange={(_, v) => v !== null && setRole(v)}
            fullWidth
            sx={{ mb: 2.5 }}
          >
            <ToggleButton value="tour-owner" sx={{ flex: 1, py: 1.1 }}>
              Tour Owner
            </ToggleButton>
            <ToggleButton value="tour-distributor" sx={{ flex: 1, py: 1.1 }}>
              Distributor
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ mb: 2.5 }} />

          <Box
            component="form"
            onSubmit={handleSubmit}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <Grid container spacing={2}>
              <TextField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>

            <TextField
              label="Email address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Company Name / Alias"
              name="company"
              value={formData.company}
              onChange={handleChange}
              fullWidth
              required
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ py: 1.3, mt: 0.5, fontSize: "0.925rem" }}
            >
              {loading ? "Creating account…" : "Create Account"}
            </Button>
          </Box>

          <Typography
            variant="body2"
            textAlign="center"
            color="text.secondary"
            mt={3}
          >
            Already have an account?{" "}
            <Link
              to="/login"
              style={{
                color: "#E50000",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Sign in
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SignUp;
